import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Loader2, Archive } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = { fontFamily: 'IBM Plex Sans, sans-serif' };

// Company-wide data export (ZIP). Reused on Company Settings and the deletion page.
export const CompanyExportCard = ({ compact = false }) => {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  if (user?.role !== 'company_admin') return null;

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/company/export.zip`, { withCredentials: true, responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : `civilx-export-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed. Please try again.');
    } finally { setExporting(false); }
  };

  const button = (
    <button type="button" onClick={exportData} disabled={exporting} data-testid="company-export-button"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#00247D] disabled:opacity-60 shrink-0">
      {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {exporting ? 'Preparing…' : 'Download data export (ZIP)'}
    </button>
  );

  if (compact) {
    return (
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-slate-50 border border-slate-200 rounded-md p-4" style={FONT} data-testid="company-export-box">
        <div className="flex-1 text-sm text-slate-700">
          <div className="font-semibold text-slate-900">Download everything first</div>
          One ZIP with CSVs of loads, drivers, payroll runs &amp; pay statements, invoices, assets and maintenance, plus every uploaded driver and load document.
        </div>
        {button}
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="company-export-card">
      <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-2 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
        <Archive className="w-5 h-5 text-[#002FA7]" /> Data Export
      </h2>
      <p className="text-sm text-slate-600 mb-4" style={FONT}>
        Your data is yours. Download a complete copy of your workspace at any time — CSV files for loads, drivers, pay
        structures, payroll runs and pay statements, invoices, assets and maintenance, plus every uploaded driver and load
        document. Handy for accountants, audits, backups or migrating systems. Exports are logged in the audit trail.
      </p>
      {button}
    </div>
  );
};

export default CompanyExportCard;
